import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import searchApiService from "../service/searchApiService";
import { useConfig } from "./useConfig";
import {
  ANCHORS_PARAM_KEY,
  FILTERS_PARAM_KEY,
  PAGE_PARAM_KEY,
  SORT_PARAM_KEY,
} from "../utils/constants";
import { dispatchGA4SearchEvent, sendViewItemListEvent } from "../utils/events";

const SEARCH_PARAM_KEY =
  window.bn_config_search_page?.search?.frontend?.general?.searchKey;

const SearchResultsContext = React.createContext();

export const SearchResultsProvider = ({ children }) => {
  const { tenant_id } = useConfig();
  const tenantId = window.bn_config_search_page.tenantId || tenant_id;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [error, setError] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState("relevancia");
  const [anchors, setAnchors] = useState([]);
  const [filters, setFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [useAnchors, setUseAnchors] = useState(false);

  const abortControllerRef = useRef();

  const updatePathQuery = useCallback(({ page, selectedFilters, sortKey }) => {
    const currentParams = new URLSearchParams(window.location.search);

    if (page && page !== 1) {
      currentParams.set(PAGE_PARAM_KEY, page);
    } else if (page === 1) {
      currentParams.delete(PAGE_PARAM_KEY);
    }

    if (sortKey && sortKey !== "relevancia") {
      currentParams.set(SORT_PARAM_KEY, sortKey);
    } else if (sortKey === "relevancia") {
      setSortKey("relevancia");
      currentParams.delete(SORT_PARAM_KEY);
    }

    if (selectedFilters !== undefined) {
      const keysToRemove = [];
      for (const [key] of currentParams) {
        if (key.startsWith(FILTERS_PARAM_KEY)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => currentParams.delete(key));

      selectedFilters?.forEach(({ name, values }) => {
        if (name === "price") {
          currentParams.set(`${FILTERS_PARAM_KEY}[price][min]`, values[0]);
          currentParams.set(`${FILTERS_PARAM_KEY}[price][max]`, values[1]);
        } else if (Array.isArray(values)) {
          values.forEach((value) => {
            currentParams.append(`${FILTERS_PARAM_KEY}[${name}][]`, value);
          });
        } else {
          currentParams.set(`${FILTERS_PARAM_KEY}[${name}][]`, values);
        }
      });
    }

    const newUrl = currentParams.toString()
      ? `?${currentParams.toString()}`
      : window.location.pathname;
    window.history.replaceState(null, "", newUrl);
    return newUrl;
  }, []);

  const updateSearchBasedOnUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);

    const searchParam = urlParams.get(SEARCH_PARAM_KEY);
    const anchorsParam = urlParams.getAll(ANCHORS_PARAM_KEY);
    const pageParam = urlParams.get(PAGE_PARAM_KEY);
    const sortParam = urlParams.get(SORT_PARAM_KEY);
    const filtersParam = [];

    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith(FILTERS_PARAM_KEY)) {
        filtersParam.push(`${key}=${value}`);
      }
    }

    const parsedFilters = [];

    filtersParam.forEach((filter) => {
      const cleanedFilter = filter
        .replace(`${FILTERS_PARAM_KEY}[`, "")
        .replace("][]", "")
        .replace("][min]", "")
        .replace("][max]", "")
        .split("=");

      const filterName = cleanedFilter[0];
      const filterValue =
        filterName === "price" ? Number(cleanedFilter[1]) : cleanedFilter[1];

      const existingFilter = parsedFilters.find((f) => f.name === filterName);
      if (existingFilter) {
        existingFilter.values.push(filterValue);
      } else {
        parsedFilters.push({
          name: filterName,
          values: [filterValue],
        });
      }
    });

    if (anchorsParam) {
      setAnchors([...anchorsParam]);
    }
    setSearchTerm(searchParam ?? "");
    setPage(pageParam ?? 1);
    setSortKey(sortParam || "relevancia");
    setSelectedFilters(parsedFilters || []);
  };

  const fetchResults = useCallback(async () => {
    if (!tenantId) return;
    if (!searchTerm && !anchors?.length) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setIsLoading(true);
    setError(false);

    try {
      const results = await searchApiService.getMetaResults({
        uuid: tenantId,
        page,
        filters: selectedFilters,
        anchors,
        word: searchTerm,
        sortKey,
      });

      if (!controller.signal.aborted) {
        if (results) {
          setResultsData(results.data);

          dispatchGA4SearchEvent({
            eventAction: "results",
            eventCount: results?.data?.meta?.results_count,
            eventProducts: results?.data?.results,
            eventLabel:
              results?.data?.meta?.term ||
              results?.data?.meta?.anchors_applied
                ?.map(
                  ({ name, tree_key }) => `${name} (${tree_key?.split(":")[0]})`
                )
                ?.join(", "),
          });

          sendViewItemListEvent({ products: results?.data?.results });
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(true);

        dispatchGA4SearchEvent({
          eventAction: "api_error",
          eventLabel: searchTerm,
        });
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
        setIsLoadingFilters(false);
      }
    }
  }, [tenantId, page, selectedFilters, anchors, searchTerm, sortKey]);

  useEffect(() => {
    const filtersAvailable = resultsData?.meta?.filters_available;
    if (filtersAvailable) {
      Object.values(filtersAvailable)?.forEach((filter) => {
        if (Object.keys(filter.values)?.length > 1) {
          return setShowFilters(true);
        }
      });

      if (selectedFilters.length > 0) {
        return setShowFilters(true);
      }
    }
  }, [resultsData, selectedFilters]);

  useEffect(() => {
    updateSearchBasedOnUrlParams();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const setters = useMemo(
    () => ({
      setAnchors,
      setFilters,
      setIsLoadingFilters,
      setPage,
      setSearchTerm,
      setSelectedFilters,
      setSortKey,
      setUseAnchors,
    }),
    []
  );

  const value = useMemo(
    () => ({
      anchors,
      fetchResults,
      filters,
      showFilters,
      sortKey,
      page,
      resultsData,
      selectedFilters,
      searchTerm,
      useAnchors,
      isLoading,
      isLoadingFilters,
      error,
      updatePathQuery,
      ...setters,
    }),
    [
      anchors,
      fetchResults,
      filters,
      showFilters,
      searchTerm,
      sortKey,
      page,
      resultsData,
      selectedFilters,
      useAnchors,
      isLoading,
      isLoadingFilters,
      error,
      updatePathQuery,
      setters,
    ]
  );

  return (
    <SearchResultsContext.Provider value={value}>
      {children}
    </SearchResultsContext.Provider>
  );
};

export const useSearchResults = () => {
  const context = useContext(SearchResultsContext);
  return context;
};
